// Page-level Zustand store for Terms & Conditions Management (PRD 14).
// Mirrors the shape of role-detail.store: mode + draft + snapshot + dirty check.
// Modes:
//   'list'   — version history table for the active tab
//   'detail' — read-only view of a single version (clicking a row)
//   'new'    — registering a new version for the active type (Save Draft / Publish)
//   'edit'   — editing a DRAFT version (Save Draft / Publish from edit mode)
//
// The store only holds UI state. Server state (types, versions) lives in react-query.

import { create } from 'zustand';

import type { TermVersion } from '../types/terms.types';

type Mode = 'list' | 'detail' | 'new' | 'edit';

// Editable subset of TermVersion. `version` is editable only in 'new' mode
// (immutable on update per PRD 14 §3.4 / contract §3.8); the store still carries
// the field so the edit-mode form can display it as read-only.
export interface TermVersionDraft {
  version: string;
  revisionNote: string;
  // YYYY-MM-DD. Empty string while the user has not picked a date.
  effectiveDate: string;
  // HTML body authored via the contenteditable RTE.
  content: string;
}

const emptyDraft = (): TermVersionDraft => ({
  version: '',
  revisionNote: '',
  effectiveDate: '',
  content: '',
});

const toDraft = (v: TermVersion): TermVersionDraft => ({
  version: v.version,
  revisionNote: v.revisionNote ?? '',
  effectiveDate: v.effectiveDate,
  content: v.content,
});

const draftsEqual = (a: TermVersionDraft, b: TermVersionDraft): boolean =>
  a.version === b.version &&
  a.revisionNote === b.revisionNote &&
  a.effectiveDate === b.effectiveDate &&
  a.content === b.content;

interface TermsDetailState {
  mode: Mode;
  // Currently active type tab. Set by the page after `useTermTypesQuery` resolves
  // (defaults to the first type), or by the user clicking a tab.
  activeTypeId: number | null;
  // The version currently being viewed/edited. `null` outside detail/edit mode.
  currentVersionId: number | null;
  // Full version snapshot for 'edit' mode. Carries the read-only meta fields
  // (status / author / registeredAt) that the editable `draft` does not hold,
  // so the edit screen can render the same read-only grid as the detail view
  // (SHCT-709: 버전 번호 stays read-only there). `null` outside edit.
  editingVersion: TermVersion | null;
  draft: TermVersionDraft | null;
  snapshot: TermVersionDraft | null;
  // Field-level error map. Keys match contract §1.8 request body field names:
  // 'version' | 'revisionNote' | 'effectiveDate' | 'content' | 'name' | 'description'.
  fieldErrors: Record<string, string>;

  setActiveTypeId: (typeId: number | null) => void;
  openList: () => void;
  openDetail: (versionId: number) => void;
  openNew: (typeId: number) => void;
  openEdit: (version: TermVersion) => void;
  updateDraft: (patch: Partial<TermVersionDraft>) => void;
  setFieldErrors: (errors: Record<string, string>) => void;
  clearFieldError: (field: string) => void;
  clearAllErrors: () => void;
  isDirty: () => boolean;
}

export const useTermsDetailStore = create<TermsDetailState>((set, get) => ({
  mode: 'list',
  activeTypeId: null,
  currentVersionId: null,
  editingVersion: null,
  draft: null,
  snapshot: null,
  fieldErrors: {},

  // Switching tab resets the view to the list for the new type. Any unsaved
  // draft is dropped — the page is expected to confirm "discard changes?" via
  // ConfirmModal before calling this when isDirty() is true.
  setActiveTypeId: (typeId) =>
    set({
      activeTypeId: typeId,
      mode: 'list',
      currentVersionId: null,
      editingVersion: null,
      draft: null,
      snapshot: null,
      fieldErrors: {},
    }),

  openList: () =>
    set({
      mode: 'list',
      currentVersionId: null,
      editingVersion: null,
      draft: null,
      snapshot: null,
      fieldErrors: {},
    }),

  openDetail: (versionId) =>
    set({
      mode: 'detail',
      currentVersionId: versionId,
      editingVersion: null,
      draft: null,
      snapshot: null,
      fieldErrors: {},
    }),

  openNew: (typeId) => {
    const empty = emptyDraft();
    set({
      mode: 'new',
      activeTypeId: typeId,
      currentVersionId: null,
      editingVersion: null,
      draft: empty,
      snapshot: { ...empty },
      fieldErrors: {},
    });
  },

  openEdit: (version) => {
    const next = toDraft(version);
    set({
      mode: 'edit',
      activeTypeId: version.typeId,
      currentVersionId: version.id,
      editingVersion: version,
      draft: next,
      snapshot: { ...next },
      fieldErrors: {},
    });
  },

  // Patch any subset of fields. Touching a field clears that field's error so
  // the inline message disappears as soon as the user edits.
  updateDraft: (patch) =>
    set((s) => {
      if (!s.draft) return {};
      const nextErrors = { ...s.fieldErrors };
      let errorsChanged = false;
      for (const key of Object.keys(patch)) {
        if (nextErrors[key]) {
          delete nextErrors[key];
          errorsChanged = true;
        }
      }
      return {
        draft: { ...s.draft, ...patch },
        ...(errorsChanged ? { fieldErrors: nextErrors } : {}),
      };
    }),

  setFieldErrors: (errors) => set({ fieldErrors: errors }),

  clearFieldError: (field) =>
    set((s) => {
      if (!s.fieldErrors[field]) return {};
      const next = { ...s.fieldErrors };
      delete next[field];
      return { fieldErrors: next };
    }),

  clearAllErrors: () => set({ fieldErrors: {} }),

  isDirty: () => {
    const { draft, snapshot } = get();
    if (!draft || !snapshot) return false;
    return !draftsEqual(draft, snapshot);
  },
}));
